"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.OrganizationWithMetadata = exports.OrganizationConstants = exports.OrganizationPrivateMetadata = exports.OrganizationTrialInformation = exports.Organization = exports.OrganizationVisibility = exports.OrganizationRole = void 0;
exports.isOrganizationRole = isOrganizationRole;
const tslib_1 = require("tslib");
const zod_1 = tslib_1.__importDefault(require("zod"));
const handle_1 = require("../handle");
const identifier_1 = require("../identifier");
const structure_1 = require("../payment/structure");
/**
 * Organization members can be owners
 */
exports.OrganizationRole = zod_1.default.enum(["owner", "member", "guest"]);
function isOrganizationRole(role) {
    return exports.OrganizationRole.safeParse(role).success;
}
exports.OrganizationVisibility = zod_1.default.enum([
    /**
     * The original visibility setting for organizations: all memberships and details of the organization are public
     */
    "public",
    /**
     * A newer visibility setting initially used for demo organizations. Firestore rules prevent the memberships from
     * being queried and the profile page can only be seen by members. These organizations are not owned by an
     * enterprise, however, and participate in the same handle-namespace as public organizations. Their existence
     * can be detected by finding their user handle in the usernames collection.
     */
    "unlisted",
    /**
     * The newest visibility setting which indicates no details of this organization (including its handle) should be
     * able to be queried by non-members (where "non-members" may take into account membership of its parent enterprise)
     */
    "private",
]);
exports.Organization = zod_1.default.object({
    uid: identifier_1.OrganizationUid,
    email: zod_1.default.string().optional(),
    bioContent: zod_1.default.string().optional(),
    customPictureKey: zod_1.default.string().optional(),
    picture: zod_1.default.string().optional(),
    websiteUrl: zod_1.default.string().optional(),
    githubHandle: zod_1.default.string().optional(),
    twitterHandle: zod_1.default.string().optional(),
    enterpriseUid: identifier_1.EnterpriseUid.optional().transform((val) => val ?? undefined),
    handle: zod_1.default.union([handle_1.OrganizationPublicHandle, handle_1.OrganizationPrivateHandle]),
    visibility: exports.OrganizationVisibility,
    displayName: zod_1.default.string(),
    partsCount: zod_1.default.number().int().nonnegative().finite(),
    documentsCount: zod_1.default.number().int().nonnegative().finite(),
    createdAt: zod_1.default.number().int().positive(),
    updatedAt: zod_1.default.number().int().positive(),
    isTest: zod_1.default
        .boolean()
        .optional()
        .transform((val) => val ?? false),
    follower_count: zod_1.default.number().int().nonnegative().finite(),
    following_count: zod_1.default.number().int().nonnegative().finite(),
    activeProductUid: zod_1.default.string().optional(),
    verified: zod_1.default.boolean().optional(),
});
exports.OrganizationTrialInformation = zod_1.default.object({
    startsAt: zod_1.default.number().finite().safe().int().min(1700000000000),
    endsAt: zod_1.default.number().finite().safe().int().min(1700000000000),
});
exports.OrganizationPrivateMetadata = zod_1.default.object({
    uid: identifier_1.OrganizationUid,
    payment: zod_1.default
        .object({
        customerUid: zod_1.default.string(),
        subscriptions: zod_1.default.object({}).optional(),
        copilotCreditSubscriptions: zod_1.default.object({}).optional(),
        generatedTrialSubscriptions: zod_1.default.object({}).optional(),
        addonSubscriptions: zod_1.default.object({}).optional(),
        subscriptionsMetadata: zod_1.default.object({}).optional(),
        stripeClientMode: structure_1.StripeClientMode.optional(),
    })
        .optional(),
    editors: zod_1.default
        .object({
        count: zod_1.default.number().int().nonnegative().finite().optional(),
        lastCountedAt: zod_1.default.number().int().positive().finite().optional(),
        needsCountAfter: zod_1.default.number().int().positive().finite().optional(),
    })
        .optional(),
    configuration: zod_1.default.object({}).optional(),
    api: zod_1.default
        .object({
        keys: zod_1.default.array(zod_1.default.object({
            createdAt: zod_1.default.number().int().positive().finite(),
            id: zod_1.default.string(),
        })),
    })
        .optional(),
});
exports.OrganizationConstants = {
    minDisplayNameLength: 3,
    maxDisplayNameLength: 255,
    minHandleLength: 3,
    maxHandleLength: 128,
};
/**
 * Decorated type, where we've filled in metadata for the organization
 */
exports.OrganizationWithMetadata = zod_1.default.object({
    ...exports.Organization.shape,
    metadata: exports.OrganizationPrivateMetadata,
});
//# sourceMappingURL=organization.js.map